import h from 'hyperscript';

import { createButton } from 'ui/button/button';
import { LOAD_START, LOAD_END, LOAD_PROGRESS } from 'ui/event-types';
import { COMPONENT_NAME as PROGRESS_SPINNER_COMPONENT_NAME, ProgressSpinner } from 'ui/progress-spinner/progress-spinner';

import { assert } from 'cadenza/utils/custom-error';

import './loading-state-cover.css';
import i18n from './view-loading-state.properties';

export const COMPONENT_NAME = 'd-loading-state-cover';
const LONG_LOADING_TIME_THRESHOLD = 1000; // ms until the loading time is considered to take "long"

/**
 * @readonly
 * @enum {string}
 */
export const LOADING_STATES = {
  LOADING: 'LOADING',
  LOADING_LONG: 'LOADING_LONG',
  LOADING_COMPLETE: 'LOADING_COMPLETE',
  CANCELED: 'CANCELED',
  OUTDATED: 'OUTDATED'
};

export const STATE_CHANGED_EVENT = 'stateChangedEvent';

const DEFAULT_CANCELABLE = false;
const DEFAULT_LOADING_STATE = '';
const DEFAULT_USER_MESSAGE = '';
const DEFAULT_SHOW_OVERLAY = false;

// exported for testing ONLY
export const CSS_CLASSES = {
  CANCEL_BUTTON: 'cancel-button',
  REFRESH_BUTTON: 'refresh-button',
  LOADING_SPINNER: PROGRESS_SPINNER_COMPONENT_NAME,
  USER_MESSAGE: 'user-message',
  OVERLAY: `${COMPONENT_NAME}--overlay`
};

const EVENT_BUS_ELEMENT_NAME = 'event-bus-element-query';

/**
 * @typedef {object} ViewLoadingState.View - _not_ a view from view.js, but a different interface
 * @property {Function} [addEventListener] - registers event listeners on the view.
 * @property {Function} [cancelLoading] - handles canceling of the loading of the view
 * @property {Function} [refresh] - handles reloading of the view
 */

export class LoadingStateCover extends HTMLElement {

  _progressSpinner; // DOM element
  _cancelButton; // DOM element
  _overlay; // DOM element
  _enableOverlay; // whether the view should be dimmed by an overlay while loading

  /**
   * Sets the internal state of the component to outdated. The user will then be able to trigger a
   * refresh of the view.
   */
  setOutdated () {
    this._state.set({
      loadingState: LOADING_STATES.OUTDATED,
      userMessage: i18n('outdatedData'),
      showOverlay: this._enableOverlay
    });
  }

  /**
   * Gets the current loading state of the view.
   *
   * @return {LOADING_STATES} - one value of LOADING_STATES enum
   */
  getLoadingState () {
    return this._state.get('loadingState');
  }

  /**
   * Show a spinner and a message to the user indicating the current state of the document: is it
   * currently loading, if the data is outdated and so on. When specified, the user can cancel the
   * loading of the view and reload it.
   *
   * @param {EventTarget} eventTarget
   * @param {object} options
   * @param {boolean} [options.enableOverlay] - whether the view should be dimmed by an overlay while loading, default: `true`
   * @param {Function} [options.onCancel] - what to execute when the Cancel button is clicked
   * @param {Function} [options.onRefresh] - what to execute when the Refresh button is clicked
   */
  constructor (eventTarget, { enableOverlay = true, onCancel, onRefresh } = {}) {
    super();

    this._enableOverlay = enableOverlay;
    this._overlay = h(`.${CSS_CLASSES.OVERLAY}`, { hidden: true });
    this._progressSpinner = new ProgressSpinner({ label: this._userMessage, size: 'xs' });
    this._userMessage = h(`span.ellipsis.${CSS_CLASSES.USER_MESSAGE}`);
    this._refreshButton = createButton(i18n('button.update'), { size: 's',
      styleClass: CSS_CLASSES.REFRESH_BUTTON,
      onclick: () => {
        assert(typeof onRefresh ===  'function',
          'API contract violation: given view must have a "refresh" method which handles reloading of the view');
        this.resetLoadingStateToDefault();
        onRefresh();
      }
    });
    this._cancelButton = createButton(i18n('button.cancel'), {
      size: 's',
      styleClass: CSS_CLASSES.CANCEL_BUTTON,
      onclick: () => {
        assert(typeof onCancel ===  'function',
          'API contract violation: given view must have a "cancelLoading" method which handles canceling of the view loading');
        this._setStateToCanceled();
        onCancel();
      }
    });
    this._viewStateIndicator = h('.d-loading-state-cover--indicator.d-alert.d-alert-small.d-alert-info', [
      this._progressSpinner, this._userMessage, this._cancelButton, this._refreshButton ]);

    const eventBus = getEventBus(eventTarget, this.getAttribute(EVENT_BUS_ELEMENT_NAME));
    this._initEventListeners(eventBus);
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
    this.append(this._overlay, this._viewStateIndicator);
    this._state.init();
  }

  _initEventListeners (eventBus) {
    eventBus.addEventListener(LOAD_START, (event) => {
      if (this._isOutdated()) {
        this._progressSpinner.hidden = false;
        return;
      }
      this._setStateToLoading(event.detail?.cancelable ?? DEFAULT_CANCELABLE);
    });
    eventBus.addEventListener(LOAD_END, () => {
      if (this._isCanceled() || this._isOutdated()) {
        this._progressSpinner.hidden = true;
        return;
      }
      this._setStateToLoadEnd();
    });
    eventBus.addEventListener(LOAD_PROGRESS, (event) => {
      this._state.set({
        cancelable: event.detail?.cancelable ?? DEFAULT_CANCELABLE
      });
    });
  }

  _state = (() => {
    const state = {
      cancelable: DEFAULT_CANCELABLE,
      loadingState: DEFAULT_LOADING_STATE,
      userMessage: DEFAULT_USER_MESSAGE,
      showOverlay: DEFAULT_SHOW_OVERLAY
    };

    // Describes how the UI is updated in response to state changes. Don't modify the UI anywhere
    // else to encapsulate that (besides the initial appending of elements).
    const stateToUi = {
      cancelable: (cancelable) => {
        if (state.loadingState === LOADING_STATES.LOADING_LONG) {
          this._cancelButton.hidden = !cancelable;
        }
      },
      userMessage: (msg) => {
        this._userMessage.textContent = msg;
      },
      showOverlay: (shouldShowOverlay) => {
        this._overlay.hidden = !shouldShowOverlay;
      },
      loadingState: (loadingState) => {
        const { LOADING, LOADING_LONG, LOADING_COMPLETE, CANCELED, OUTDATED } = LOADING_STATES;

        switch (loadingState) {
          case LOADING:
            setTimeout(() => {
              // If we are still loading after a certain amount of time, it's considered to be a
              // long time.
              if (state.loadingState === LOADING) {
                this._setStateToLongLoading();
              }
            }, LONG_LOADING_TIME_THRESHOLD);
            this.hidden = false;
            this._cancelButton.hidden = true;
            this._overlay.hidden = !state.showOverlay;
            this._progressSpinner.hidden = false;
            this._refreshButton.hidden = true;
            break;

          case LOADING_LONG:
            this._cancelButton.hidden = !state.cancelable;
            break;

          case CANCELED:
            this._cancelButton.hidden = true;
            this._overlay.hidden = !state.showOverlay;
            this._refreshButton.hidden = false;
            break;

          case OUTDATED:
            this.hidden = false;
            this._cancelButton.hidden = true;
            this._overlay.hidden = !state.showOverlay;
            this._progressSpinner.hidden = true;
            this._refreshButton.hidden = false;
            break;

          case DEFAULT_LOADING_STATE:
            this.hidden = true;
            break;

          case LOADING_COMPLETE:
            this.hidden = true;
            break;

          default:
            throw new Error(`Trying to set an invalid loading state. Given state name: ${loadingState}`);
        }
        this.dispatchEvent(new CustomEvent(STATE_CHANGED_EVENT, { detail: { loadingState } }));
      }
    };
    return {
      init: () => {
        Object.entries(state).map(([ key, value ]) => {
          stateToUi[key](value);
        });
      },
      set: (newState) => {
        Object.entries(newState).map(([ key, value ]) => {
          assert(Object.keys(state).includes(key),
            `Given key is not in state, have you made a typo? Given key: ${key}`);

          if (state[key] !== value) {
            assert(stateToUi[key], 'key not listed in stateToUI mapping ' + key);
            state[key] = value;
            stateToUi[key](value);
          }
        });
      },
      get: (propName) => state[propName]
    };
  })();

  _setStateToLoading (cancelable) {
    this._state.set({
      cancelable,
      userMessage: i18n('loading'),
      loadingState: LOADING_STATES.LOADING,
      showOverlay: this._enableOverlay
    });
  }

  _setStateToLoadEnd () {
    this._state.set({
      cancelable: DEFAULT_CANCELABLE,
      userMessage: DEFAULT_USER_MESSAGE,
      loadingState: LOADING_STATES.LOADING_COMPLETE,
      showOverlay: false
    });
  }

  _setStateToLongLoading () {
    this._state.set({
      userMessage: i18n('loadingLong'),
      loadingState: LOADING_STATES.LOADING_LONG
    });
  }

  _setStateToCanceled () {
    this._state.set({
      userMessage: i18n('outdatedData'),
      loadingState: LOADING_STATES.CANCELED
    });
  }

  resetLoadingStateToDefault () {
    this._state.set({
      loadingState: DEFAULT_LOADING_STATE,
      showOverlay: false
    });
  }

  resetOutdatedState () {
    if (this._isOutdated()) {
      this._state.set({
        loadingState: DEFAULT_LOADING_STATE,
        showOverlay: false
      });
    }
  }

  _isCanceled () {
    return this._state.get('loadingState') === LOADING_STATES.CANCELED;
  }

  _isOutdated () {
    return this._state.get('loadingState') === LOADING_STATES.OUTDATED;
  }

  // for testing ONLY
  exposeInternalState (propName) {
    return this._state.get(propName);
  }

}

function getEventBus (eventBus, eventBusElementQuery) {
  if (eventBus) {
    return eventBus;
  } else {
    assert(eventBusElementQuery, 'Make sure to define the "event-bus-element-query" attribute or pass in the eventBus');
    const eventBus = document.querySelector(eventBusElementQuery);
    assert(eventBus, 'Event bus cannot be null');
    return eventBus;
  }
}

customElements.define(COMPONENT_NAME, LoadingStateCover);
